import React, { useState } from "react";
import {
    Alert, Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogContentText, DialogTitle, TextField, Typography,
} from "@mui/material";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Cancel from "@mui/icons-material/Cancel";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import { approveStaffPhoto, rejectStaffPhoto, removeStaffPhoto, uploadStaffPhoto } from "../../services/staffAdminService";
import { StaffUser } from "../../types/staff";
import {
    PHOTO_ACCEPT, PHOTO_REJECT_PRESETS, errorMessageOf, isValidRejectReason, resizePhoto, staffInitials,
} from "../../utils/staffPhoto";

export type StaffPhotoFields = Pick<StaffUser,
    'id' | 'firstName' | 'lastName' | 'photoUrl' | 'pendingPhotoUrl' | 'photoStatus' | 'photoRejectReason'>;

interface StaffPhotoPanelProps {
    staff: StaffPhotoFields;
    /** staff:manage — without it the photos are view-only. */
    canManage: boolean;
    /** The admin is viewing their own record: nobody reviews their own photo. */
    isSelf: boolean;
    /** Called after every successful change so the caller can refresh the row and dialog. */
    onChanged: () => Promise<void> | void;
}

const StaffPhotoPanel: React.FC<StaffPhotoPanelProps> = ({ staff, canManage, isSelf, onChanged }) => {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [rejectOpen, setRejectOpen] = useState(false);
    const [reason, setReason] = useState("");
    const [removeOpen, setRemoveOpen] = useState(false);

    const name = [staff.firstName, staff.lastName].filter(Boolean).join(" ");

    const run = async (action: () => Promise<unknown>, fallback: string) => {
        setBusy(true);
        setError(null);
        try {
            await action();
            await onChanged();
        } catch (e) {
            setError(errorMessageOf(e, fallback));
        } finally {
            setBusy(false);
        }
    };

    const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = ""; // lets the same file be picked again after an error
        if (!file) return;
        await run(async () => uploadStaffPhoto(staff.id, await resizePhoto(file)), "Could not upload the photo");
    };

    const submitReject = async () => {
        setRejectOpen(false);
        await run(() => rejectStaffPhoto(staff.id, reason.trim()), "Could not reject the photo");
        setReason("");
    };

    const submitRemove = async () => {
        setRemoveOpen(false);
        await run(() => removeStaffPhoto(staff.id), "Could not remove the photo");
    };

    return (
        <Box data-testid="staff-photo-panel">
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Photo</Typography>
            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", alignItems: "flex-start" }}>
                <Box sx={{ textAlign: "center" }}>
                    <Avatar
                        src={staff.photoUrl || undefined}
                        alt={`${name} — approved photo`}
                        sx={{ width: 88, height: 88, mx: "auto", fontSize: 28 }}
                    >
                        {staffInitials(staff.firstName, staff.lastName)}
                    </Avatar>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                        {staff.photoUrl ? "Approved photo" : "No approved photo"}
                    </Typography>
                </Box>

                {staff.pendingPhotoUrl && (
                    <Box sx={{ textAlign: "center" }}>
                        <Avatar
                            src={staff.pendingPhotoUrl}
                            alt={`${name} — photo waiting for approval`}
                            variant="rounded"
                            sx={{ width: 88, height: 88, mx: "auto", border: 2, borderStyle: "dashed", borderColor: "warning.main" }}
                        />
                        <Chip label="Waiting for approval" color="warning" size="small" sx={{ mt: 0.5 }} />
                        {canManage && (isSelf ? (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, maxWidth: 200 }}>
                                Another admin must review your own photo.
                            </Typography>
                        ) : (
                            <Box sx={{ display: "flex", gap: 1, justifyContent: "center", mt: 1 }}>
                                <Button
                                    size="small" variant="contained" color="success" startIcon={<CheckCircle />}
                                    disabled={busy}
                                    onClick={() => run(() => approveStaffPhoto(staff.id), "Could not approve the photo")}
                                >
                                    Approve
                                </Button>
                                <Button
                                    size="small" variant="outlined" color="error" startIcon={<Cancel />}
                                    disabled={busy}
                                    onClick={() => setRejectOpen(true)}
                                >
                                    Reject
                                </Button>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>

            {staff.photoStatus === "REJECTED" && staff.photoRejectReason && (
                <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
                    Last photo rejected: {staff.photoRejectReason}
                </Typography>
            )}

            {canManage && (
                <>
                    <Box sx={{ display: "flex", gap: 1, mt: 1.5, alignItems: "center", flexWrap: "wrap" }}>
                        <Button size="small" variant="outlined" component="label" startIcon={<PhotoCamera />} disabled={busy}>
                            Upload photo
                            <input
                                hidden type="file" accept={PHOTO_ACCEPT} disabled={busy}
                                data-testid="staff-photo-input" onChange={handleFile}
                            />
                        </Button>
                        {staff.photoUrl && (
                            <Button size="small" color="error" startIcon={<DeleteOutline />} disabled={busy} onClick={() => setRemoveOpen(true)}>
                                Remove photo
                            </Button>
                        )}
                        {busy && <CircularProgress size={18} />}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                        An upload here is approved at once and replaces any photo waiting for approval.
                    </Typography>
                </>
            )}

            {error && <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>{error}</Alert>}

            <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Reject photo</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2, mt: 0.5 }}>
                        {PHOTO_REJECT_PRESETS.map((preset) => (
                            <Chip
                                key={preset} label={preset} onClick={() => setReason(preset)}
                                color={reason === preset ? "primary" : "default"}
                            />
                        ))}
                    </Box>
                    <TextField
                        label="Reason" fullWidth multiline minRows={2}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        inputProps={{ maxLength: 200 }}
                        helperText={`${reason.trim().length}/200 — at least 3 characters`}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
                    <Button color="error" variant="contained" disabled={!isValidRejectReason(reason)} onClick={submitReject}>
                        Reject photo
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={removeOpen} onClose={() => setRemoveOpen(false)} maxWidth="xs">
                <DialogTitle>Remove photo?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {name}'s approved photo will be removed everywhere it shows.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRemoveOpen(false)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={submitRemove}>Remove</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default StaffPhotoPanel;
